// What a "primary action" button (grid card, category listing, product page) should do for a
// given product — the single place this decision is made, so a subscription plan or a service
// with a "subscribe and save" option can't silently get added straight into the plain cart from
// somewhere that has no room to ask "which tier, and how do you want to pay for it". Used by every
// surface that renders such a button: ProductGrid.tsx, product-card.tsx, ProductBlocks.tsx.
// Relative (not @/) so node --test can load this module directly (product-action.test.ts).
import { activeSubscriptionTiers } from '../types/subscription-tiers.ts';

export type ProductPrimaryAction =
    | { type: 'add_to_cart' }
    /** No room here to pick a tier/payment mode — send the customer to the product page, where
     *  the full picker (or, for a subscription_plan, the single "Wykup abonament" button) lives. */
    | { type: 'view_product' };

type ProductLike = {
    product_type?: string;
    service_config?: any;
    price_on_request?: boolean;
    /** Set by the /products listing (ProductListItem), which doesn't send service_config. */
    uses_configurator?: boolean;
    has_subscription_tiers?: boolean;
    service_pricing_type?: string | null;
};

/** unit / weight / flat / area for a D2D service (null for anything else). The /products listing
 *  sends it as service_pricing_type, the product page inside service_config. */
export function servicePricingType(product: ProductLike | null | undefined): string | null {
    if (!product || product.product_type !== 'service') return null;
    const raw = product.service_pricing_type ?? product.service_config?.pricing_type;
    return raw === 'weight' || raw === 'flat' || raw === 'area' ? raw : 'unit';
}

/** One measured piece of a per-m² service (dywan 200 × 300 cm). */
export interface ServiceMeasure { width_cm: number; length_cm: number; }

/** m² charged for one piece — never below the service's minimum (service_config.min_quantity m²).
 *  Mirrors billable_area_m2() in backend/shop/logic/service_pricing.py, which sets the real price. */
export function serviceBillableArea(product: ProductLike | null | undefined, measure: ServiceMeasure): number {
    const area = Math.round((measure.width_cm * measure.length_cm) / 100) / 100;
    const minimum = Number(product?.service_config?.min_quantity) || 0;
    return Math.max(area, minimum);
}

/** "Minimalny wsad" — the smallest quantity (kg for a per-kg service, pieces for a per-piece one)
 *  the service is taken in; less is billed as this much. Mirrors service_min_quantity() in
 *  backend/shop/logic/service_pricing.py, which rejects an order below it. */
export function serviceMinQuantity(product: (ProductLike & { service_min_quantity?: number | null }) | null | undefined): number {
    const pricing = servicePricingType(product);
    // flat: one charge; area: the minimum is in m² per piece (serviceBillableArea), not a count.
    if (!pricing || pricing === 'flat' || pricing === 'area') return 1;
    const raw = Number(product?.service_min_quantity ?? product?.service_config?.min_quantity);
    return Number.isFinite(raw) && raw > 1 ? Math.ceil(raw) : 1;
}

/** Whether the product is bought through the live configurator — a 'configurable' product, or a
 *  D2D service whose options are switched on ("pranie / pranie + prasowanie", "ekspres +10 zł").
 *  Mirrors uses_configurator() in backend/shop/logic/products.py, which recomputes the price. */
export function usesConfigurator(product: ProductLike | null | undefined): boolean {
    if (!product) return false;
    if (product.product_type === 'configurable') return true;
    if (product.product_type !== 'service') return false;
    return product.uses_configurator === true || product.service_config?.configurator === true;
}

function hasSubscriptionTiers(product: ProductLike): boolean {
    return product.has_subscription_tiers === true || activeSubscriptionTiers(product.service_config).length > 0;
}

export function getProductPrimaryAction(product: ProductLike | null | undefined): ProductPrimaryAction {
    if (!product) return { type: 'add_to_cart' };
    // "Cena na zapytanie": no cart at all — the product page carries the contact options.
    if (product.price_on_request) return { type: 'view_product' };
    if (product.product_type === 'subscription_plan') return { type: 'view_product' };
    // A Pick & Pack box needs its parts chosen in the configurator on the product page.
    if (product.product_type === 'pick_pack') return { type: 'view_product' };
    if (product.product_type === 'service' && hasSubscriptionTiers(product)) {
        return { type: 'view_product' };
    }
    // Options to choose first — only the product page has room for them.
    if (product.product_type === 'service' && usesConfigurator(product)) return { type: 'view_product' };
    // Washing by the kilo: the customer states the estimated weight on the product page — a
    // one-tap "1 kg" from a card is never what they order.
    if (servicePricingType(product) === 'weight') return { type: 'view_product' };
    // Per m²: the piece's dimensions are entered on the product page.
    if (servicePricingType(product) === 'area') return { type: 'view_product' };
    return { type: 'add_to_cart' };
}

/** Whether a listing card's buttons can only lead to the product page — something must be picked
 *  there first (a variant, options, a seat, the weight, a subscription tier). Such a card shows one
 *  "Wybierz opcje"-style button: a "Kup teraz" next to it could do nothing more than the same jump,
 *  and used to quick-add a plan or a price-on-request product straight into the cart instead. */
export function cardOpensProductPage(product: (ProductLike & { has_seat_map?: boolean }) | null | undefined): boolean {
    if (!product) return false;
    if (getProductPrimaryAction(product).type === 'view_product') return true;
    if (product.product_type === 'variable' || usesConfigurator(product)) return true;
    // Every ticket: the event (and, with a seat map, the seat) is picked on the product page — a
    // quick-added ticket carried no event, so checkout now refuses it (it used to sell with no PDF).
    return product.product_type === 'ticket';
}

/** What a "Cena na zapytanie" product shows instead of its price — the merchant's own text
 *  (e.g. "Dostępna wkrótce") or the translated default. */
export function priceOnRequestLabel(
    product: { price_on_request_label?: string | null } | null | undefined,
    t: (key: string, fallback?: string) => string,
): string {
    return product?.price_on_request_label?.trim() || t('product.price_on_request', 'Cena na zapytanie');
}
